import { useNavigate } from "react-router-dom";
import { Coins, ExternalLink, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import { RISK_LABEL } from "@/lib/clientFormat";
import { uahFull, dmy } from "@/lib/client360Labels";
import type { Client360Envelope } from "@/api/clients";

/**
 * Client360 Overview 2.0 (Этап 1) — shared BI strip (holding-grain, BI-owned,
 * read-only). Renders the 6-KPI secondary strip from the compact envelope.
 *
 * Source-aware (§2): "з BI · снапшот <дата>" freshness badge. blockStatus
 * discipline: BI partial/down → muted warning banner, NEVER empty/zeroed KPIs.
 * The credit figure (BUNDLE 1 STEP 5) renders whenever the envelope carries it
 * (WS-2: customContractView flag retired — ON-поведінка стала безумовною).
 *
 * `onCreateAction` (optional) wires the overdue-KPI "Створити дію →" CTA; absent
 * on non-overview tabs so the strip stays a pure read-only context band there.
 */
export function ClientBiStrip({
  env,
  onCreateAction,
}: {
  env: Client360Envelope;
  onCreateAction?: () => void;
}) {
  const navigate = useNavigate();
  const id = env.header.id;
  const cf = env.creditFigure;

  const credit =
    cf ? (
      <div className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2">
        <div className="flex items-center gap-2 text-sm">
          <Coins className="h-4 w-4 text-primary" />
          <span className="text-muted-foreground">
            {cf.label ?? "Кредитний ліміт за основним активним договором"}:
          </span>
          <span className="font-semibold tabular-nums">
            {cf.determined && cf.creditLimit != null
              ? `${new Intl.NumberFormat("uk-UA").format(cf.creditLimit)} ${cf.currency || "₴"}`
              : cf.determined
                ? "—"
                : "не визначено"}
          </span>
        </div>
        {cf.determined && cf.contractId && (
          <button
            onClick={() => navigate(clientRoutes.contract(id, cf.contractId!))}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            {cf.contractNumber ? `№${cf.contractNumber}` : "договір"}
            <ExternalLink className="h-3 w-3" />
          </button>
        )}
      </div>
    ) : null;

  if (!env.biAvailability.available) {
    return (
      <div className="space-y-3">
        {credit}
        <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm text-warning-foreground">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          {env.biAvailability.reason ?? "BI-показники тимчасово недоступні."} Картка клієнта актуальна.
        </div>
      </div>
    );
  }

  const b = env.biStrip;
  const overdue = b.holdingOverdueTotal ?? 0;

  const metrics: Array<{ label: string; value: string; danger?: boolean; cta?: React.ReactNode }> = [
    { label: "Виручка 365д", value: uahFull(b.holdingRevenue365d) },
    { label: "Виручка 30д", value: uahFull(b.holdingRevenue30d) },
    {
      label: "Борг (всього)",
      value: uahFull(b.holdingArTotal),
      cta: env.allowedActions.canViewLegalEntities ? (
        <button onClick={() => navigate(clientRoutes.entities(id))} className="text-[11px] text-primary hover:underline">
          Деталі →
        </button>
      ) : undefined,
    },
    {
      label: "Прострочено",
      value: uahFull(b.holdingOverdueTotal),
      danger: overdue > 0,
      cta:
        overdue > 0 && onCreateAction ? (
          <button onClick={onCreateAction} className="text-[11px] text-primary hover:underline">
            Створити дію →
          </button>
        ) : undefined,
    },
    { label: "ABC", value: b.holdingAbc ?? "—" },
    { label: "Ризик", value: b.holdingRiskStatus ? RISK_LABEL[b.holdingRiskStatus] : "—" },
  ];

  return (
    <div className="space-y-1.5">
      {credit}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {metrics.map((m) => (
          <div key={m.label} className="rounded-lg border bg-card px-3 py-2.5">
            <div className="text-[11px] text-muted-foreground">{m.label}</div>
            <div className={cn("mt-0.5 text-base font-semibold tabular-nums", m.danger && "text-destructive")}>
              {m.value}
            </div>
            {m.cta && <div className="mt-0.5">{m.cta}</div>}
          </div>
        ))}
      </div>
      {env.biStrip.biSyncedAt && (
        <div className="pt-0.5">
          <span className="inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] text-muted-foreground">
            ⟳ з BI · снапшот {dmy(env.biStrip.biSyncedAt)}
          </span>
        </div>
      )}
    </div>
  );
}
